import type { ComponentPropsWithoutRef, ReactElement, ReactNode } from 'react';
import { Children, Fragment, useMemo } from 'react';

import { Icon } from '~/chords/Icon';

import type {
  BreadcrumbsEmphasis,
  BreadcrumbsSize,
} from './BreadcrumbsContext';
import { BreadcrumbsContext } from './BreadcrumbsContext';
import { breadcrumbsRootVariants } from './variants';

export interface BreadcrumbsRootProps
  extends Omit<ComponentPropsWithoutRef<'nav'>, 'className'> {
  children: NonNullable<ReactNode>;
  size?: BreadcrumbsSize;
  emphasis?: BreadcrumbsEmphasis;
}

export function BreadcrumbsRoot({
  children,
  size = 'default',
  emphasis = 'default',
  ...delegatedProps
}: BreadcrumbsRootProps) {
  const childrenWithSeparators = useMemo(() => {
    const validChildren = Children.toArray(children).filter(
      Boolean,
    ) as ReactElement[];

    const separatorSize = size === 'heading' ? '2xs' : 'xs';

    return validChildren.map((child, index) => {
      return (
        <Fragment key={child.key}>
          {index ? (
            <Icon
              type="chevron-right"
              size={separatorSize}
              role="presentation"
              aria-hidden
            />
          ) : null}
          {child}
        </Fragment>
      );
    });
  }, [children, size]);

  if (!childrenWithSeparators?.length) {
    return null;
  }

  return (
    <BreadcrumbsContext.Provider value={{ size, emphasis }}>
      <nav {...delegatedProps} className={breadcrumbsRootVariants({ size })}>
        {childrenWithSeparators}
      </nav>
    </BreadcrumbsContext.Provider>
  );
}

BreadcrumbsRoot.displayName = 'Breadcrumbs.Root';
